// src/components/cofounder/MessageRenderer.js
import React from 'react';

/**
 * Render markdown-like content with basic formatting
 */
const MessageRenderer = ({ content }) => {
  if (!content) return null;

  const renderContent = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let inCodeBlock = false;
    let codeContent = [];
    let listItems = [];
    let inList = false;

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc pl-5 my-2 space-y-1">
            {listItems.map((item, i) => (
              <li key={i} className="text-sm">{renderInline(item)}</li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Code blocks
      if (line.trim().startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={`code-${i}`} className="bg-gray-800 text-green-300 rounded-lg p-3 my-2 overflow-x-auto text-xs">
              <code>{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          flushList();
          inCodeBlock = true;
        }
        continue;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        continue;
      }

      // List items
      if (line.trim().match(/^[-*]\s/)) {
        inList = true;
        listItems.push(line.trim().replace(/^[-*]\s/, ''));
        continue;
      }

      // Numbered list items
      if (line.trim().match(/^\d+\.\s/)) {
        inList = true;
        listItems.push(line.trim().replace(/^\d+\.\s/, ''));
        continue;
      }

      // Non-list line: flush pending list
      flushList();

      // Empty line
      if (line.trim() === '') {
        elements.push(<div key={`br-${i}`} className="h-2" />);
        continue;
      }

      // Headers
      if (line.startsWith('### ')) {
        elements.push(<h4 key={`h3-${i}`} className="font-semibold text-sm mt-3 mb-1">{renderInline(line.slice(4))}</h4>);
        continue;
      }
      if (line.startsWith('## ')) {
        elements.push(<h3 key={`h2-${i}`} className="font-bold text-sm mt-3 mb-1">{renderInline(line.slice(3))}</h3>);
        continue;
      }
      if (line.startsWith('# ')) {
        elements.push(<h2 key={`h1-${i}`} className="font-bold text-base mt-3 mb-1">{renderInline(line.slice(2))}</h2>);
        continue;
      }

      // Regular paragraph
      elements.push(<p key={`p-${i}`} className="text-sm my-1">{renderInline(line)}</p>);
    }

    flushList();
    return elements;
  };

  const renderInline = (text) => {
    // Bold
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      // Inline code
      const codeParts = part.split(/(`[^`]+`)/g);
      return codeParts.map((cp, j) => {
        if (cp.startsWith('`') && cp.endsWith('`')) {
          return <code key={`${i}-${j}`} className="bg-gray-100 text-purple-700 px-1 rounded text-xs">{cp.slice(1, -1)}</code>;
        }
        return cp;
      });
    });
  };

  return <div className="message-content">{renderContent(content)}</div>;
};

export default MessageRenderer;
